// Design tokens — the only source of colours, spacing, radii, type and motion.
// Values and their contrast checks are documented in docs/DESIGN_SYSTEM.md.

export type ThemeName = 'light' | 'dark';

export type SemanticRole =
  | 'income'
  | 'expense'
  | 'savings'
  | 'investment'
  | 'debt'
  | 'warning'
  | 'success'
  | 'error'
  | 'neutral';

export type ThemeColors = {
  readonly canvas: string;
  /** Image layers laid over the canvas: OMGLoft's grain, tiled. */
  readonly canvasImage: string;
  /** The same grain, softer, over cards and controls (OMGLoft: nothing is smooth). */
  readonly grainSoft: string;
  readonly sidebar: string;
  readonly sidebarInk: string;
  readonly sidebarMuted: string;
  readonly sidebarActive: string;
  readonly sidebarActiveInk: string;
  readonly heroSurface: string;
  readonly heroInk: string;
  readonly heroMuted: string;
  readonly heroAccent: string;
  readonly surface: string;
  readonly elevated: string;
  readonly overlay: string;
  readonly scrim: string;
  readonly hover: string;
  readonly pressed: string;
  readonly selected: string;
  readonly border: string;
  readonly borderStrong: string;
  readonly inkPrimary: string;
  readonly inkSecondary: string;
  readonly inkMuted: string;
  readonly inkDisabled: string;
  readonly accent: string;
  readonly accentHover: string;
  readonly accentPressed: string;
  readonly accentSubtle: string;
  /** The accent as text and small icons (≥ 4.5:1); `accent` itself fills buttons and marks. */
  readonly accentInk: string;
  /** Section labels in small caps (Playloft's coral), ≥ 4.5:1 on every surface. */
  readonly labelInk: string;
  readonly onAccent: string;
  readonly focusRing: string;
  readonly skeleton: string;
  readonly chartGrid: string;
  readonly chartAxis: string;
  /** Text colours, ≥ 4.5:1 on every surface. */
  readonly fg: Readonly<Record<SemanticRole, string>>;
  /** Fills for marks, icons and chart series with a meaning. */
  readonly fill: Readonly<Record<SemanticRole, string>>;
  /** Soft backgrounds for badges and banners. */
  readonly soft: Readonly<Record<SemanticRole, string>>;
  /** Categorical chart palette, fixed order; labels also identify every series. */
  readonly series: readonly string[];
  /** Sequential ramp for magnitude (heatmaps): empty day, then four steps of one hue. */
  readonly heat: readonly string[];
};

/** OMGLoft's grain (its /img/grain-*.svg): grey fractal noise at one alpha, a 200 px tile. */
const grain = (seed: number, alpha: number) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g' x='0' y='0' width='100%25' height='100%25' color-interpolation-filters='sRGB'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' seed='${seed}' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeComponentTransfer%3E%3CfeFuncA type='linear' slope='0' intercept='${alpha}'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)'/%3E%3C/svg%3E")`;

export const colors: Readonly<Record<ThemeName, ThemeColors>> = {
  // Light (05.10.2026, the owner: "the same look as OMGLoft"): its toned-down light theme — a warm stone page
  // with grain, off-white panels with stone hairlines, near-black text and its red; green income and coral
  // spending, charts keep their own hues.
  light: {
    canvas: '#e7e4df',
    canvasImage: grain(23, 0.11),
    grainSoft: grain(41, 0.045),
    sidebar: '#f3f1ed',
    sidebarInk: '#1d1d22',
    sidebarMuted: '#5f6068',
    sidebarActive: '#d8262e',
    sidebarActiveInk: '#ffffff',
    heroSurface: '#f8f6f2',
    heroInk: '#1d1d22',
    heroMuted: '#5f6068',
    heroAccent: '#d8262e',
    surface: '#f8f6f2',
    elevated: '#f8f6f2',
    overlay: '#f8f6f2',
    scrim: 'rgba(20,20,30,0.36)',
    hover: '#eeebe6',
    pressed: '#e4e1db',
    selected: '#fdecec',
    border: '#dcd8d1',
    borderStrong: '#cdc8c0',
    inkPrimary: '#1d1d22',
    inkSecondary: '#46474e',
    inkMuted: '#5f6068',
    inkDisabled: '#8d8e95',
    accent: '#d8262e',
    accentHover: '#c11f27',
    accentPressed: '#a11a21',
    accentSubtle: '#fdecec',
    accentInk: '#b3262c',
    labelInk: '#b3262c',
    onAccent: '#ffffff',
    focusRing: '#e8868a',
    skeleton: 'rgba(20,20,30,0.07)',
    chartGrid: '#e4e1db',
    chartAxis: '#cdc8c0',
    // A shade darker than before OMGLoft's stone page (05.10.2026): ≥ 4.5:1 on it too.
    fg: {
      income: '#17734a',
      expense: '#b43b27',
      savings: '#0d6d87',
      investment: '#5b4bc4',
      debt: '#925607',
      warning: '#8e5900',
      success: '#17734a',
      error: '#bc2f29',
      neutral: '#5c6578',
    },
    // Marks at full chroma (the owner: "juicy, not pastel").
    fill: {
      income: '#10b981',
      expense: '#f9603e',
      savings: '#06b6d4',
      investment: '#8b5cf6',
      debt: '#f59e0b',
      warning: '#fbbf24',
      success: '#10b981',
      error: '#ef4444',
      neutral: '#a3aab8',
    },
    soft: {
      income: '#e8f5ee',
      expense: '#fdeeea',
      savings: '#e5f4f7',
      investment: '#efedfc',
      debt: '#fdf2e2',
      warning: '#fef5e1',
      success: '#e8f5ee',
      error: '#fdecec',
      neutral: '#eeebe6',
    },
    // Validated (dataviz validator, 29.09.2026): lightness band, chroma, adjacent CVD ≥ 8,
    // normal vision ≥ 15; aqua, yellow and pink sit under 3:1, so charts label their series.
    series: ['#4f5bd5', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#8a3ec2', '#e34948'],
    heat: ['#eeebe6', '#fbd9cf', '#f6ae98', '#ec7f63', '#c9533a'],
  },
  // Dark (05.10.2026, OMGLoft's dark theme): a deep navy page with grain, navy panels with a visible hairline,
  // cool off-white text, its brighter red with pink text for it. Data keeps its own hues: charts do not turn red
  // with the brand.
  dark: {
    canvas: '#0a0f19',
    canvasImage: grain(23, 0.09),
    grainSoft: grain(41, 0.05),
    sidebar: '#0d1320',
    sidebarInk: '#f1f3f7',
    sidebarMuted: '#a5afc2',
    sidebarActive: '#e5262e',
    sidebarActiveInk: '#ffffff',
    heroSurface: '#161f30',
    heroInk: '#f1f3f7',
    heroMuted: '#a5afc2',
    heroAccent: '#ff8a8f',
    surface: '#161f30',
    elevated: '#1a2436',
    overlay: '#161f30',
    scrim: 'rgba(3,6,12,0.66)',
    hover: '#1f2a3e',
    pressed: '#283550',
    selected: '#3a1c25',
    border: '#283449',
    borderStrong: '#36445f',
    inkPrimary: '#f1f3f7',
    inkSecondary: '#c9d0dd',
    inkMuted: '#a5afc2',
    inkDisabled: '#7a859b',
    accent: '#e5262e',
    accentHover: '#f03a42',
    accentPressed: '#c11f27',
    accentSubtle: '#3a1c25',
    accentInk: '#ff8a8f',
    labelInk: '#ff8a8f',
    onAccent: '#ffffff',
    focusRing: '#ff9aa0',
    skeleton: 'rgba(241,243,247,0.07)',
    chartGrid: '#1f2a3e',
    chartAxis: '#36445f',
    // Juicy, not pastel (the owner: "instead of pastel colours use juicy ones"): full-chroma
    // steps that still keep ≥ 4.5:1 as text on every dark surface.
    fg: {
      income: '#34d399',
      expense: '#ff7a59',
      savings: '#22d3ee',
      investment: '#a78bfa',
      debt: '#f59e0b',
      warning: '#fbbf24',
      success: '#34d399',
      error: '#f87171',
      neutral: '#adb6c5',
    },
    fill: {
      income: '#10b981',
      expense: '#f9603e',
      savings: '#06b6d4',
      investment: '#8b5cf6',
      debt: '#f59e0b',
      warning: '#fbbf24',
      success: '#10b981',
      error: '#ef4444',
      neutral: '#697282',
    },
    soft: {
      income: 'rgba(16,185,129,0.17)',
      expense: 'rgba(249,96,62,0.17)',
      savings: 'rgba(6,182,212,0.17)',
      investment: 'rgba(139,92,246,0.2)',
      debt: 'rgba(245,158,11,0.17)',
      warning: 'rgba(251,191,36,0.17)',
      success: 'rgba(16,185,129,0.17)',
      error: 'rgba(239,68,68,0.18)',
      neutral: 'rgba(241,243,247,0.06)',
    },
    series: ['#6f7be8', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9a74e0', '#e66767'],
    heat: ['#1f2a3e', '#4a2c26', '#7a3b2c', '#b85538', '#f08e6e'],
  },
};

export const space = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

/** OMGLoft's scale: small elements 8, inputs and buttons 11, cards 14, banners 18 — one scale everywhere. */
export const radius = { xs: 6, sm: 8, md: 11, lg: 14, xl: 18, full: 9999 } as const;

export type TypeStyle = {
  readonly size: number;
  readonly lineHeight: number;
  readonly weight: number;
  readonly letterSpacing?: number;
};

export const typography = {
  // Geist, OMGLoft's type (a variable font from the app's own files, styles/global.css).
  fontFamily: "'Geist', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  styles: {
    hero: { size: 40, lineHeight: 46, weight: 600, letterSpacing: -1.2 },
    display: { size: 28, lineHeight: 34, weight: 600, letterSpacing: -0.5 },
    titleLg: { size: 20, lineHeight: 28, weight: 600, letterSpacing: -0.3 },
    title: { size: 15, lineHeight: 22, weight: 560, letterSpacing: -0.1 },
    body: { size: 14, lineHeight: 20, weight: 400 },
    bodyStrong: { size: 14, lineHeight: 20, weight: 520 },
    bodySm: { size: 13, lineHeight: 18, weight: 400 },
    caption: { size: 12, lineHeight: 16, weight: 500 },
    overline: { size: 11, lineHeight: 14, weight: 600, letterSpacing: 0.7 },
    amountLg: { size: 28, lineHeight: 34, weight: 600, letterSpacing: -0.7 },
    amountMd: { size: 20, lineHeight: 26, weight: 600, letterSpacing: -0.4 },
    amount: { size: 14, lineHeight: 20, weight: 520 },
  } satisfies Record<string, TypeStyle>,
} as const;

/** OMGLoft's: a soft shadow under every panel (`sm`), a stronger one under menus and windows (`lg`). */
export const shadows: Readonly<Record<ThemeName, { sm: string; md: string; lg: string }>> = {
  light: {
    sm: '0 1px 2px rgba(20,20,30,0.05), 0 6px 18px rgba(20,20,30,0.05)',
    md: '0 1px 2px rgba(20,20,30,0.06), 0 8px 22px rgba(20,20,30,0.08)',
    lg: '0 12px 32px rgba(20,20,30,0.12)',
  },
  dark: {
    sm: '0 1px 2px rgba(0,0,0,0.3), 0 8px 22px rgba(0,0,0,0.25)',
    md: '0 1px 2px rgba(0,0,0,0.32), 0 10px 26px rgba(0,0,0,0.32)',
    lg: '0 16px 40px rgba(0,0,0,0.5)',
  },
};

export const motion = {
  duration: { instant: 90, fast: 150, base: 220, slow: 300, chart: 600 },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    enter: 'cubic-bezier(0, 0, 0, 1)',
    exit: 'cubic-bezier(0.3, 0, 1, 1)',
    /** Figures and charts settling into their new values. */
    settle: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
} as const;

export const zIndex = {
  sticky: 10,
  dropdown: 30,
  overlay: 40,
  modal: 50,
  toast: 60,
  tooltip: 70,
} as const;

const kebab = (name: string) => name.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

/** CSS custom properties for one theme, e.g. `--color-canvas: #f5f5f2;`. */
export function themeVariables(theme: ThemeName): Record<string, string> {
  const c = colors[theme];
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(c)) {
    if (typeof value === 'string') vars[`--color-${kebab(key)}`] = value;
  }
  for (const group of ['fg', 'fill', 'soft'] as const) {
    for (const [role, value] of Object.entries(c[group])) vars[`--${group}-${role}`] = value;
  }
  c.series.forEach((value, index) => (vars[`--series-${index + 1}`] = value));
  c.heat.forEach((value, index) => (vars[`--heat-${index}`] = value));
  const s = shadows[theme];
  vars['--shadow-sm'] = s.sm;
  vars['--shadow-md'] = s.md;
  vars['--shadow-lg'] = s.lg;
  return vars;
}

function staticVariables(): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(space))
    vars[`--space-${String(key).replace('.', '_')}`] = `${value}px`;
  for (const [key, value] of Object.entries(radius)) vars[`--radius-${key}`] = `${value}px`;
  for (const [key, value] of Object.entries(motion.duration))
    vars[`--duration-${key}`] = `${value}ms`;
  for (const [key, value] of Object.entries(motion.easing)) vars[`--ease-${key}`] = value;
  for (const [key, value] of Object.entries(zIndex)) vars[`--z-${key}`] = String(value);
  vars['--font-sans'] = typography.fontFamily;
  for (const [name, style] of Object.entries(typography.styles)) {
    const t = style as TypeStyle;
    vars[`--text-${kebab(name)}-size`] = `${t.size}px`;
    vars[`--text-${kebab(name)}-line`] = `${t.lineHeight}px`;
    vars[`--text-${kebab(name)}-weight`] = String(t.weight);
    vars[`--text-${kebab(name)}-tracking`] = `${t.letterSpacing ?? 0}px`;
  }
  return vars;
}

const block = (selector: string, vars: Record<string, string>) =>
  `${selector} {\n${Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n')}\n}`;

/**
 * Full stylesheet: static tokens, light theme by default, dark theme from the OS setting
 * unless the user forced light, and an explicit dark override for the in-app toggle.
 */
export function tokensCss(): string {
  const dark = themeVariables('dark');
  return [
    block(':root', { ...staticVariables(), ...themeVariables('light'), 'color-scheme': 'light' }),
    `@media (prefers-color-scheme: dark) {\n${block(':root:not([data-theme="light"])', { ...dark, 'color-scheme': 'dark' })}\n}`,
    block(':root[data-theme="dark"]', { ...dark, 'color-scheme': 'dark' }),
    `@media (prefers-reduced-motion: reduce) {\n  :root { --duration-instant: 0ms; --duration-fast: 0ms; --duration-base: 0ms; --duration-slow: 0ms; --duration-chart: 0ms; }\n}`,
  ].join('\n\n');
}
